import React from 'react'
import {FormattedDate} from 'react-intl'
import styled from '@emotion/styled'
import {imageProvider} from 'utils/imageProvider'
import {getNumberOfNights} from 'utils/offers'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {isUnnamedRoomName} from '@vio/core/src/room/business/utils/roomless'
import {useRoomDisplayName} from '@vio/shared/src/accommodation/RoomDisplayName'
import {RoomlessDisclaimer} from '@vio/shared/src/accommodation/RoomlessDisclaimer'
import {
  GuestSplit,
  GuestSplitText
} from '@vio/shared/src/search/GuestPicker/GuestSplitText'

interface DealDetailsOverviewProps {
  isRoomless: boolean
  roomName: string
  roomImage?: string
  checkIn: string
  checkOut: string
  numberOfGuests: number
  numberOfRooms: number
  /** When set, shows "X adults, Y children" instead of the guest total. */
  guestSplit?: GuestSplit
}

const RoomNameWrapper = styled(Text)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`
export const DealDetailsOverview = ({
  isRoomless,
  roomName,
  roomImage,
  checkIn,
  checkOut,
  numberOfGuests,
  numberOfRooms,
  guestSplit
}: DealDetailsOverviewProps) => {
  const numberOfNights = getNumberOfNights(checkIn, checkOut)
  const generatedRoomName = useRoomDisplayName(roomName, {
    numberOfGuests,
    numberOfRooms
  })
  const roomDisplayName = isRoomless ? generatedRoomName : roomName
  return (
    <Box display="flex" gap="s400">
      {roomImage ? (
        <Box width="70px">
          <Image
            isResponsive
            imageProvider={imageProvider}
            size="mobileSmall"
            borderRadius="lg"
            url={roomImage}
            alt={roomDisplayName}
          />
        </Box>
      ) : null}

      <Box flex="1">
        <Box display="flex" flexDirection="column" gap="s250">
          <RoomNameWrapper variant="titleM" data-testid="room-name-title">
            <FormattedMessageWrapper
              id="bundleRoomName"
              defaultMessage={'{numberOfRooms}x {roomName}'}
              values={{numberOfRooms, roomName: roomDisplayName}}
            />
          </RoomNameWrapper>

          <Box display="flex" alignItems="center" gap="s300">
            <Icon name="Calendar" />
            <Text>
              <FormattedMessageWrapper
                id="dealDetails.nights"
                defaultMessage="{checkIn} - {checkOut} ({count, plural, one {1 night} other {# nights}})"
                values={{
                  checkIn: (
                    <FormattedDate
                      value={checkIn}
                      day="numeric"
                      month="short"
                      timeZone="UTC"
                    />
                  ),
                  checkOut: (
                    <FormattedDate
                      value={checkOut}
                      day="numeric"
                      month="short"
                      timeZone="UTC"
                    />
                  ),
                  count: numberOfNights
                }}
              />
            </Text>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap="s300">
          <Icon name="Person" />
          <Text>
            {guestSplit ? (
              <GuestSplitText {...guestSplit} />
            ) : (
              <FormattedMessageWrapper
                id="numberOfGuests"
                defaultMessage="{count, plural, one {1 guest} other {# guests}}"
                values={{count: numberOfGuests}}
              />
            )}
          </Text>
        </Box>

        {isRoomless && isUnnamedRoomName(roomName) && (
          <RoomlessDisclaimer size="md" />
        )}
      </Box>
    </Box>
  )
}
